import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button as AntButton, DatePicker, Icon, Input, Popover, Tooltip } from 'antd';
import styled from 'styled-components';
import moment from 'moment';
import {
  getStagesFor,
  STAGE_DONE,
  STAGE_NA,
  normalizeStages,
  countStagesMarked,
  getStagesTiming,
  getStagesAgeTone,
  getNextStageKey,
  stagesEqual,
  getStageNotes,
  areStagesLoaded,
} from '~/_domains/societario/helpers';
import { request } from '~/components/Instance';
import { useSocietarioStages } from '~/_domains/societario/stagesConfig';

// Editor das etapas do processo, no lugar do antigo campo "Pendência".
//
// Recebe `value` / `onChange` do getFieldDecorator. Clicar de novo no botao ja
// marcado desfaz a marcacao — a etapa volta a pendente.
const TONES = {
  done: { solid: '#52c41a', line: '#389e0d', soft: '#f6ffed' },
  na: { solid: '#ff4d4f', line: '#cf1322', soft: '#fff1f0' },
  pending: { solid: '#faad14', line: '#d48806', soft: '#fffbe6' },
};

// O cronômetro envelhece de cor: cinza, âmbar a partir de 15 dias, vermelho a
// partir de 30, verde quando o processo fecha.
const CLOCK_TONES = {
  fresh: { border: '#e8e8e8', background: '#fff', color: '#8c8c8c' },
  warn: { border: '#ffe58f', background: '#fffbe6', color: '#d48806' },
  late: { border: '#ffa39e', background: '#fff1f0', color: '#cf1322' },
  finished: { border: '#b7eb8f', background: '#f6ffed', color: '#389e0d' },
};

const Panel = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  margin-bottom: 8px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
`;

const Title = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #262626;
`;

const Counter = styled.span`
  margin-left: auto;
  font-size: 12px;
  color: #8c8c8c;
`;

const Clock = styled.span`
  margin-left: 10px;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid ${(props) => props.tone.border};
  background: ${(props) => props.tone.background};
  color: ${(props) => props.tone.color};
`;

// Num ticket novo a marcação só vira registro no Salvar. Sem este aviso, quem
// marcasse quatro etapas e fechasse o drawer perderia tudo sem saber.
const Unsaved = styled.span`
  margin-left: 10px;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid #91d5ff;
  background: #e6f7ff;
  color: #096dd9;
`;

const Saved = styled.span`
  margin-left: 10px;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  white-space: nowrap;
  border: 1px solid #e8e8e8;
  background: #fff;
  color: #8c8c8c;
`;

const Row = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 11px 16px;
  transition: background 0.2s;

  & + & {
    border-top: 1px solid #f7f7f7;
  }

  &:hover {
    background: #fafafa;
  }
`;

// Fio da linha do tempo: liga um marcador ao seguinte, sem sobrar ponta antes
// da primeira etapa nem depois da ultima.
const Connector = styled.span`
  position: absolute;
  left: 27px;
  width: 2px;
  background: #f0f0f0;
  top: ${(props) => (props.first ? '50%' : '0')};
  bottom: ${(props) => (props.last ? '50%' : '0')};
`;

const Marker = styled.span`
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  transition: all 0.2s;
  border: 2px solid ${(props) => props.tone};
  background: ${(props) => (props.filled ? props.tone : '#fff')};
  color: ${(props) => (props.filled ? '#fff' : props.tone)};
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
  padding: 0 12px;
`;

const Name = styled.div`
  font-size: 13px;
  line-height: 18px;
  color: ${(props) => (props.struck ? '#8c8c8c' : '#262626')};
  text-decoration: ${(props) => (props.struck ? 'line-through' : 'none')};
`;

const Meta = styled.div`
  font-size: 11px;
  line-height: 16px;
  color: #bfbfbf;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Actions = styled.div`
  flex-shrink: 0;
  display: flex;
`;

const Button = styled.button`
  width: ${(props) => props.width}px;
  margin-left: 8px;
  padding: 0 10px;
  border-radius: 14px;
  font-size: 12px;
  line-height: 22px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${(props) => (props.active ? props.tone : '#d9d9d9')};
  background: ${(props) => (props.active ? props.tone : '#fff')};
  color: ${(props) => (props.active ? '#fff' : '#8c8c8c')};
  font-weight: ${(props) => (props.active ? 600 : 400)};

  &:hover:not(:disabled) {
    border-color: ${(props) => props.tone};
    color: ${(props) => (props.active ? '#fff' : props.tone)};
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const NoteLine = styled.div`
  font-size: 12px;
  line-height: 16px;
  margin-top: 2px;
  color: #595959;
  white-space: pre-wrap;
`;

const IconButton = styled.button`
  margin-left: 8px;
  width: 26px;
  height: 24px;
  border-radius: 12px;
  cursor: pointer;
  border: 1px solid ${(props) => (props.active ? '#1890ff' : '#d9d9d9')};
  background: ${(props) => (props.active ? '#e6f7ff' : '#fff')};
  color: ${(props) => (props.active ? '#1890ff' : '#8c8c8c')};

  &:hover:not(:disabled) {
    border-color: #1890ff;
    color: #1890ff;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const MAX_NOTE = 255;

// Num ticket que já existe, cada clique grava na hora (PUT /dashtasks/{id}/stages):
// antes a marcação só valia no Salvar do formulário, e qualquer campo
// obrigatório vazio barrava o salvamento sem que se percebesse. Num ticket novo
// ainda não há onde gravar — aí a marcação segue com o formulário, como antes.
const Stages = (props) => {
  useSocietarioStages();
  const dispatch = useDispatch();
  const category = props.category;
  // Ticket finalizado usa a lista congelada na conclusão.
  const frozen = Array.isArray(props.stageList) && props.stageList.length > 0;
  const scope = frozen ? props.stageList : category;
  const taskId = props.taskId || null;
  const instant = !!taskId;
  const list = getStagesFor(scope);
  const value = normalizeStages(props.value, scope);
  const disabled = !!props.disabled;
  const onChange = props.onChange;
  const usersById = useSelector((state) => state.usersById);

  const [baseline, setBaseline] = useState(props.initial);
  const [notes, setNotes] = useState(getStageNotes({ stage_notes: props.notes }));
  const [savingKey, setSavingKey] = useState(null);
  const [editingDate, setEditingDate] = useState(null);
  const [noteDraft, setNoteDraft] = useState({ key: null, text: '' });

  useEffect(() => {
    setBaseline(props.initial);
  }, [props.initial]);

  useEffect(() => {
    setNotes(getStageNotes({ stage_notes: props.notes }));
  }, [props.notes]);

  const persist = (key, payload) => {
    setSavingKey(key);

    return request('put', '/dashtasks/' + taskId + '/stages', payload, false)
      .then((response) => {
        const task = response.data && response.data.data;
        const fields = (task && task.custom_fields) || {};
        const saved = normalizeStages(fields.stages, scope);

        setBaseline(saved);
        setNotes(getStageNotes(fields));
        if (onChange) onChange(saved);
        // Atualiza o card no painel na mesma hora.
        dispatch({ type: '@@DRAWERSOCIETARIO/UPDATE_SUCCESS', payload: { data: { data: task } } });
      })
      .finally(() => setSavingKey(null));
  };

  const setStage = (key, status) => {
    if (disabled || !onChange || savingKey) {
      return;
    }

    const next = { ...value };

    if (next[key] && next[key].status === status) {
      delete next[key];
    } else {
      // `at` e `by` sao carimbados no servidor; daqui vai so a intencao.
      next[key] = {
        status: status,
        at: next[key] ? next[key].at : null,
        by: next[key] ? next[key].by : null,
      };
    }

    if (instant) {
      persist(key, { stages: next });
      return;
    }

    onChange(next);
  };

  const setDate = (key, date) => {
    setEditingDate(null);
    if (!date || !value[key]) return;

    const next = { ...value };
    next[key] = { ...value[key], date: date.format('YYYY-MM-DD') };
    persist(key, { stages: next });
  };

  const saveNote = (key, text) => {
    setNoteDraft({ key: null, text: '' });
    persist(key, { notes: { [key]: text } });
  };

  const userName = (id) => {
    const user = id && usersById ? usersById[id] : null;
    return user && user.name ? user.name : null;
  };

  const describe = (current, isNext) => {
    if (!current) {
      return isNext ? 'Etapa da vez' : 'Aguardando';
    }

    const parts = [current.status === STAGE_DONE ? 'Concluído' : 'Marcada como não aplicável'];
    const by = userName(current.by);

    if (by) {
      parts.push('por ' + by);
    }

    if (current.at) {
      parts.push(
        current.edited_by
          ? 'em ' + moment(current.at).format('DD/MM/YYYY')
          : 'em ' + moment(current.at).format('DD/MM/YYYY [às] HH:mm')
      );
    }

    const editor = userName(current.edited_by);
    if (current.edited_by) {
      parts.push('· data ajustada' + (editor ? ' por ' + editor : ''));
    }

    return parts.join(' ');
  };

  const noteEditor = (key) => (
    <div style={{ width: 300 }}>
      <Input.TextArea
        autoFocus
        rows={3}
        maxLength={MAX_NOTE}
        value={noteDraft.text}
        placeholder="Ex.: protocolo 123456, em exigência na Junta..."
        onChange={(event) => setNoteDraft({ key: key, text: event.target.value })}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <span style={{ fontSize: 11, color: '#bfbfbf' }}>{noteDraft.text.length}/{MAX_NOTE}</span>
        <span>
          {notes[key] && (
            <AntButton size="small" type="link" onClick={() => saveNote(key, '')}>
              Remover
            </AntButton>
          )}
          <AntButton size="small" type="primary" onClick={() => saveNote(key, noteDraft.text)}>
            Salvar
          </AntButton>
        </span>
      </div>
    </div>
  );

  const marked = countStagesMarked(value, scope);
  const timing = getStagesTiming(value, scope);
  const nextKey = getNextStageKey(value, scope);
  const clockTone = CLOCK_TONES[getStagesAgeTone(timing)] || CLOCK_TONES.fresh;
  const dirty = !instant && !stagesEqual(baseline, value, scope);

  if (!frozen && !areStagesLoaded()) {
    return (
      <Panel>
        <Header>
          <Title>Etapas do processo</Title>
          <Counter>
            <Icon type="loading" style={{ marginRight: 6 }} />
            Carregando as etapas do escritório...
          </Counter>
        </Header>
      </Panel>
    );
  }

  return (
    <Panel>
      <Header>
        <Title>Etapas do processo</Title>
        <Counter>
          {marked} de {list.length} resolvidas
        </Counter>
        {timing && (
          <Tooltip
            title={
              timing.running
                ? 'Contando desde a primeira etapa marcada'
                : 'Tempo total, congelado com todas as etapas resolvidas'
            }
          >
            <Clock tone={clockTone}>
              <Icon type="clock-circle" style={{ marginRight: 4 }} />
              {timing.days} {timing.days === 1 ? 'dia' : 'dias'}
            </Clock>
          </Tooltip>
        )}
        {frozen && (
          <Tooltip title="Ticket concluído: as etapas são as que valiam na conclusão. Mudanças na lista do tipo não o afetam.">
            <Saved>
              <Icon type="lock" style={{ marginRight: 4 }} />
              Lista da conclusão
            </Saved>
          </Tooltip>
        )}
        {instant && (
          <Tooltip title="Cada marcação é gravada na hora, sem precisar do Salvar">
            <Saved>
              <Icon type={savingKey ? 'loading' : 'cloud'} style={{ marginRight: 4 }} />
              {savingKey ? 'Salvando...' : 'Salvo automaticamente'}
            </Saved>
          </Tooltip>
        )}
        {dirty && (
          <Tooltip title="As marcações só valem depois de clicar em Salvar">
            <Unsaved>
              <Icon type="exclamation-circle" style={{ marginRight: 4 }} />
              Não salvo
            </Unsaved>
          </Tooltip>
        )}
      </Header>

      {list.map((stage, index) => {
        const current = value[stage.key];
        const status = current ? current.status : null;
        const tone = status ? TONES[status] : TONES.pending;
        const isNext = stage.key === nextKey;
        const note = notes[stage.key];
        const busy = savingKey === stage.key;

        return (
          <Row key={stage.key}>
            <Connector first={index === 0 ? 1 : 0} last={index === list.length - 1 ? 1 : 0} />
            {/* Marcador cheio = resolvida ou etapa da vez; vazado = ainda longe. */}
            <Marker tone={tone.solid} filled={status || isNext ? 1 : 0}>
              {busy && <Icon type="loading" />}
              {!busy && status === STAGE_DONE && <Icon type="check" />}
              {!busy && status === STAGE_NA && <Icon type="close" />}
              {!busy && !status && index + 1}
            </Marker>
            <Content>
              <Name struck={status === STAGE_NA ? 1 : 0}>{stage.label}</Name>
              <Meta>
                {describe(current, isNext)}
                {instant && current && !disabled && editingDate !== stage.key && (
                  <AntButton
                    type="link"
                    size="small"
                    style={{ height: 16, padding: '0 0 0 6px', fontSize: 11, lineHeight: '16px' }}
                    onClick={() => setEditingDate(stage.key)}
                  >
                    alterar data
                  </AntButton>
                )}
              </Meta>
              {editingDate === stage.key && (
                <DatePicker
                  size="small"
                  open
                  autoFocus
                  allowClear={false}
                  format="DD/MM/YYYY"
                  defaultValue={current && current.at ? moment(current.at) : moment()}
                  disabledDate={(date) => date && date.isAfter(moment().endOf('day'))}
                  onChange={(date) => setDate(stage.key, date)}
                  onOpenChange={(open) => { if (!open) setEditingDate(null); }}
                  style={{ marginTop: 4 }}
                />
              )}
              {note && note.text && (
                <NoteLine>
                  <Icon type="message" style={{ marginRight: 4, color: '#1890ff' }} />
                  {note.text}
                </NoteLine>
              )}
            </Content>
            <Actions>
              <Button
                type="button"
                width={110}
                tone={TONES.done.solid}
                active={status === STAGE_DONE ? 1 : 0}
                aria-pressed={status === STAGE_DONE}
                disabled={disabled || !!savingKey}
                onClick={() => setStage(stage.key, STAGE_DONE)}
              >
                <Icon type="check" style={{ marginRight: 5 }} />
                Concluído
              </Button>
              <Button
                type="button"
                width={130}
                tone={TONES.na.solid}
                active={status === STAGE_NA ? 1 : 0}
                aria-pressed={status === STAGE_NA}
                disabled={disabled || !!savingKey}
                onClick={() => setStage(stage.key, STAGE_NA)}
              >
                <Icon type="stop" style={{ marginRight: 5 }} />
                Não aplicável
              </Button>
              {instant && (
                <Popover
                  trigger="click"
                  placement="bottomRight"
                  title={'Observação · ' + stage.label}
                  visible={noteDraft.key === stage.key}
                  onVisibleChange={(visible) => setNoteDraft(
                    visible
                      ? { key: stage.key, text: note && note.text ? note.text : '' }
                      : { key: null, text: '' }
                  )}
                  content={noteEditor(stage.key)}
                >
                  <Tooltip title={note ? 'Editar observação' : 'Adicionar observação'}>
                    <IconButton
                      type="button"
                      aria-label="Observação"
                      active={note ? 1 : 0}
                      disabled={disabled || !!savingKey}
                    >
                      <Icon type="message" />
                    </IconButton>
                  </Tooltip>
                </Popover>
              )}
            </Actions>
          </Row>
        );
      })}
    </Panel>
  );
};

export default Stages;
